import { useState } from 'react';
import { Lock } from 'lucide-react';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import UpgradePrompt from './UpgradePrompt';

interface LockedFeatureButtonProps {
  feature: string;
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
  variant?: 'primary' | 'secondary';
  disabled?: boolean;
}

/**
 * Button that checks feature access and shows upgrade prompt if locked
 * Use this for action buttons that require specific plan features
 */
export default function LockedFeatureButton({
  feature,
  onClick,
  children,
  className = '',
  variant = 'primary',
  disabled = false,
}: LockedFeatureButtonProps) {
  const { hasFeature } = useFeatureAccess();
  const [showPrompt, setShowPrompt] = useState(false);
  const hasAccess = hasFeature(feature);

  const baseStyles =
    'inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed';

  const variantStyles = {
    primary: hasAccess
      ? 'bg-ocean-600 text-white hover:bg-ocean-700 disabled:hover:bg-ocean-600'
      : 'bg-gray-400 text-white cursor-not-allowed',
    secondary: hasAccess
      ? 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:hover:bg-white'
      : 'border border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed',
  };

  const handleClick = () => {
    if (disabled) return;

    if (hasAccess && onClick) {
      onClick();
    } else if (!hasAccess) {
      setShowPrompt(true);
    }
  };

  return (
    <>
      <button
        onClick={handleClick}
        disabled={disabled && hasAccess}
        className={`${baseStyles} ${variantStyles[variant]} ${className}`}
      >
        {!hasAccess && <Lock className="h-4 w-4" />}
        {children}
      </button>

      <UpgradePrompt
        isOpen={showPrompt}
        onClose={() => setShowPrompt(false)}
        feature={feature}
      />
    </>
  );
}
